import React from "react";
import { Box, Typography } from "@mui/material";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import MarkChatReadOutlinedIcon from "@mui/icons-material/MarkChatReadOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import Shimmer from "../../../../../components/crm-dashboard/Shimmer";
import InsightCard from "../InsightCard";
import {
  CADENCE_COLORS,
  formatCadenceLine,
  formatDate,
  spendTrendCopy,
  frequencyTrendCopy,
  upsellCopy,
  cadenceCopy,
  lastContactMeta,
  lastBookingMeta,
} from "../../churnUtils";
import { ACCENT, PINK_BORDER } from "../../churnStyles";

// Compact insights column docked beside the chat thread. Reads the same
// churnStore.insights data the Insights tab uses — no extra fetching here
// beyond what ChatModal dispatches.
const ChatInsightsSidebar = ({ data, loading }) => {
  if (loading && !data) {
    return (
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Shimmer width="100%" height={90} radius="14px" />
        <Shimmer width="100%" height={90} radius="14px" />
        <Shimmer width="100%" height={90} radius="14px" />
      </Box>
    );
  }

  if (!data) {
    return (
      <Typography sx={{ color: "#9CA3AF", fontSize: "13px", p: 2, textAlign: "center" }}>
        Insights are not available for this customer yet.
      </Typography>
    );
  }

  const spend = data.spend_trend;
  const freq = data.frequency_trend;
  const cadence = data.cadence;
  const contactMeta = lastContactMeta(data.last_contact);
  // Falls back to cadence.last_visit_date on payloads without last_booking.
  const bookingMeta = lastBookingMeta(data.last_booking || cadence);

  return (
    <Box
      sx={{
        p: 2,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 0.5, borderBottom: `1px solid ${PINK_BORDER}` }}>
        <QueryStatsOutlinedIcon sx={{ color: ACCENT, fontSize: 18 }} />
        <Typography sx={{ fontSize: "14px", color: ACCENT, fontFamily: "helvetica-lt-std-bold" }}>
          Customer Insights
        </Typography>
      </Box>

      <InsightCard
        compact
        icon={MarkChatReadOutlinedIcon}
        color={contactMeta.color}
        title="Last contact"
        headline={contactMeta.headline}
        detail={contactMeta.detail}
        action={contactMeta.action}
      />
      <InsightCard
        compact
        icon={EventNoteOutlinedIcon}
        color={bookingMeta.color}
        title="Last booking"
        headline={bookingMeta.headline}
        detail={bookingMeta.detail}
        action={bookingMeta.action}
      />

      {cadence && (
        <InsightCard
          compact
          icon={EventAvailableOutlinedIcon}
          color={CADENCE_COLORS[cadence.cadence_status] || "#9CA3AF"}
          title="Next booking expected"
          headline={formatCadenceLine({ ...cadence, visit_count: data.maturity?.visit_count })}
          action={cadenceCopy(cadence)}
        />
      )}
      {(data.upsell || []).map((u) => (
        <InsightCard
          compact
          key={`${u.have_service}-${u.suggest_service}`}
          icon={AddShoppingCartOutlinedIcon}
          color="#A72C68"
          title="Upsell opportunity"
          headline={u.suggest_service}
          detail={`Based on ${u.sample_size} customers`}
          action={upsellCopy(u)}
        />
      ))}
      {spend && (
        <InsightCard
          compact
          icon={spend.pct_change < 0 ? TrendingDownOutlinedIcon : TrendingUpOutlinedIcon}
          color={spend.pct_change < 0 ? "#EF4444" : "#16A34A"}
          title="Spend trend (90d vs prior 90d)"
          headline={`$${Math.round(spend.last_90d).toLocaleString()} vs $${Math.round(
            spend.prior_90d
          ).toLocaleString()}`}
          action={spendTrendCopy(spend)}
        />
      )}
      {freq && (
        <InsightCard
          compact
          icon={EventRepeatOutlinedIcon}
          color={freq.recent_per_90d < freq.alltime_per_90d * 0.7 ? "#F59E0B" : "#16A34A"}
          title="Booking frequency"
          headline={`${freq.recent_per_90d}/90d recently · ${freq.alltime_per_90d}/90d all-time`}
          action={frequencyTrendCopy(freq)}
        />
      )}
      {data.top_services?.length > 0 && (
        <InsightCard
          compact
          icon={StarBorderOutlinedIcon}
          color="#A72C68"
          title="Top services"
          headline={data.top_services
            .slice(0, 5)
            .map((s) => `${s.name} (${s.count})`)
            .join(" · ")}
          action="These are the anchor services — lead with them and cross-sell around them."
        />
      )}
      {data.maturity && (
        <InsightCard
          compact
          icon={CakeOutlinedIcon}
          color="#0EA5E9"
          title="Account maturity"
          headline={`${data.maturity.visit_count} visits since ${formatDate(
            data.maturity.first_visit_date
          )}`}
          action={
            data.maturity.visit_count >= 5
              ? "Established account — protect the base, look for upsell openings."
              : "Newer account — focus on a solid second and third visit."
          }
        />
      )}
    </Box>
  );
};

export default ChatInsightsSidebar;
